<template>
  <section class="shadow-sm bg-gray-50 rounded-md border mt-8">
    <div
      id="header"
      class="flex justify-center md:justify-between flex-col md:flex-row items-center px-5 py-2 border-b"
    >
      <div class="flex items-center gap-4 h-[48px]" v-if="data?.name">
        <label>
          <h5 :class="`py-4 w-full cursor-pointer capitalize text-base`">
            {{
              $t("labels.generic", {
                pt: `Plano ${getMaterialTypePT(data?.name)}`,
                en: `${data?.name} Plan`,
              })
            }}
          </h5>
        </label>
        <Button
          v-if="role === 'TEACHER'"
          class="btn-outline h-[32px] p-button-sm"
          @click="redirectToPage()"
        >
          {{
            $t("labels.generic", {
              pt: `Selecionar objetos`,
              en: `Choose objects`,
            })
          }}
        </Button>
        <p v-if="error" class="text-red-600 text-sm">
          {{
            $t("labels.generic", {
              pt: `Por favor adicionar ao menos um objeto a este plano!`,
              en: `Please add at least one object to this lesson plan!`,
            })
          }}
        </p>
      </div>
      <div class="flex items-center gap-4 h-[48px] w-1/2" v-else>
        <Skeleton class="w-1/2 border-round h-1rem" />
      </div>
      <h6 v-if="data?.name">
        {{ data?.materials.length }}
        {{
          $t("labels.generic", {
            pt: `objeto${data?.materials.length == 1 ? "" : "s"}`,
            en: `object${data?.materials.length == 1 ? "" : "s"}`,
          })
        }}
      </h6>
    </div>
    <div
      id="content"
      class="h-[300px] overflow-auto scroll-style bg-white"
      v-if="data?.name"
    >
      <Draggable
        v-model="data.materials"
        itemKey="index"
        @end="onEnd"
        :disabled="true"
      >
        <template #item="{ element }">
          <div
            :class="`flex items-center gap-5 border-b-2 border-b-gray-100 p-5 bg-white hover:bg-blue-50 cursor-pointer`"
          >
            <div
              class="flex items-center justify-between w-full flex-wrap gap-5"
            >
              <div class="flex items-center gap-5">
                <div class="flex justify-center">
                  <v-icon
                    :name="getIcon(element.file?.mimetype)"
                    class="text-h-primary-500"
                    :scale="1.3"
                  />
                </div>
                <div class="flex flex-col">
                  <p class="text-lg font-bold text-gray-800 mb-2 line-clamp-1">
                    {{ element.name }}
                  </p>
                  <div
                    class="flex flex-wrap gap-3 md:divide-x divide-gray-500 text-gray-500 text-sm"
                  >
                    <p class="text-sm capitalize">
                      {{
                        $t("labels.generic", {
                          pt: `Classificação: ${getLearningGroupTypePT(
                            element.learningGroupType
                          )}`,
                          en: `Classification: ${element.learningGroupType} |`,
                        })
                      }}
                    </p>

                    <TagMaterials :material="element" />
                  </div>
                </div>
              </div>
              <!-- <OrderButtons
                :all-data="data?.materials"
                :data="element"
                :index="index"
                @on-order="onOrder($event, index, data?.materials)"
              /> -->
            </div>
          </div>
        </template>
      </Draggable>
      <div class="py-8" v-if="!data?.materials || data?.materials.length === 0">
        <EmptyData
          :data="!data?.materials || data?.materials.length === 0"
          :title="emptyState.title"
          :label="emptyState.description"
          :image="emptyState.image"
          imgSize="h-32"
        />
      </div>
    </div>
    <div
      id="content"
      class="h-[300px] overflow-auto scroll-style bg-white"
      v-else
    >
      <div class="h-[300px] flex justify-center items-center">
        <Loader :loader="data?.name == ''" />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { LessonPlan } from "@/models/lessonPlan.model";
import { Material } from "@/models/material.model";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { MaterialsUtils } from "@/utils/materials.util";
import { Messages } from "@/utils/messages.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { computed, defineComponent } from "vue";
export default defineComponent({
  name: "List",
  props: {
    data: Object,
    error: Boolean,
    loaderList: Boolean,
    role: String,
  },
  computed: {
    go(): GoToRoute {
      return new GoToRoute();
    },
    materialsUtils(): MaterialsUtils {
      return new MaterialsUtils();
    },
    rest(): MaterialsRest {
      return new MaterialsRest();
    },
  },
  data() {
    return {
      emptyState: {
        image: "empty-materials-plan.svg",
        title: this.$t("labels.generic", {
          pt: `Nenhum objeto atribuído`,
          en: `No object assigned`,
        }),
        description: this.$t("labels.generic", {
          pt: `Clique em “Selecionar objetos”, para atribuir os objetos que você já adicionou a esse plano de estudos.`,
          en: `Click on “Select objects”, to assign the objects that you have already added to this study plan.`,
        }),
      } as any,
      initialData: null as null | LessonPlan,
    };
  },
  watch: {
    data(val) {
      this.initialData = JSON.parse(JSON.stringify(val?.materials));
    },
  },
  setup() {
    const generics = computed(() => new GenericsUtils());
    return { generics };
  },
  methods: {
    redirectToPage(): void {
      let routePageParams = {
        objectiveId: this.data?.learningObjective,
        courseId: this.$route.params.courseId,
        chapterId: this.$route.params.chapterId,
        lessonPlanId: this.data?.id,
      };
      this.go.redirectToPage("lessonPlan", routePageParams);
    },
    getMaterialTypePT(lessonPlanName: string): string {
      return this.materialsUtils.getMaterialTypePT(lessonPlanName);
    },
    getIcon(icon: string): string {
      return this.materialsUtils.getIcon(icon);
    },
    getLearningGroupTypePT(learningGroup: string): string {
      return this.materialsUtils.getLearningGroupTypePT(learningGroup);
    },
    onOrder(event: string, index: number, data: Material[]): void {
      const prevIndex = index;
      const newIndex = event === "up" ? index - 1 : index + 1;
      const prevMaterial: Material = this.data?.materials[prevIndex];
      const nextMaterial: Material = this.data?.materials[newIndex];

      if (nextMaterial?.learningGroupType === prevMaterial?.learningGroupType) {
        this.generics.onOrder(event, index, data, this.updateOrder);
      } else {
        this.resetData();
      }
    },
    updateOrder(prev: number, next: number): void {
      const nextMaterial: Material = this.data?.materials[next];
      const prevMaterial: Material = this.data?.materials[prev];
      const newOrder: Material[] = [
        { ...prevMaterial, index: prev },
        { ...nextMaterial, index: next },
      ];

      this.rest.orderMaterials(this.data?.id, newOrder).subscribe({
        next: () => {
          this.$toast.add(ToastFactory.success(Messages.MATERIAL_ORDER));
        },
      });
    },
    onEnd(event: { oldIndex: number; newIndex: number }): void {
      const prevIndex = event.oldIndex;
      const newIndex = event.newIndex;
      const prevMaterial: Material = this.data?.materials[prevIndex];
      const nextMaterial: Material = this.data?.materials[newIndex];

      if (
        prevIndex !== newIndex &&
        nextMaterial.learningGroupType === prevMaterial.learningGroupType
      ) {
        this.updateOrder(prevIndex, newIndex);
      } else {
        this.resetData();
      }
    },
    resetData(): void {
      this.data!.materials = JSON.parse(JSON.stringify(this.initialData));
      this.$toast.add(ToastFactory.error(Messages.MATERIAL_ORDER_ERROR));
    },
  },
});
</script>
